<script setup lang="ts">
/**
 * studioPRO Enterprise - Hauptanwendungskomponente
 *
 * Diese Komponente dient als Root-Komponente für die gesamte Anwendung.
 * Sie initialisiert das Theme-System, die Authentifizierung und
 * die grundlegende Layout-Struktur der Anwendung.
 *
 * @version 1.0.0
 * @author MiniMax Agent
 * @license MIT
 */

import { onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/modules/auth/stores/auth.store'
import { useThemeStore } from '@/modules/settings/stores/theme.store'
import AdminLayout from '@/layouts/AdminLayout.vue'
import AuthLayout from '@/core/components/layout/AuthLayout.vue'
import StandaloneLayout from '@/layouts/StandaloneLayout.vue'

/**
 * Store-Instanzen für Authentifizierung und Theme-Management
 */
const authStore = useAuthStore()
const themeStore = useThemeStore()

/**
 * Computed Property zur Bestimmung des aktuellen Layouts
 * basierend auf dem Authentifizierungsstatus
 */
const route = useRoute()
const currentLayout = computed(() => {
  if (!authStore.isAuthenticated) return AuthLayout
  // Eigenständige Seiten (Offline-Übungsbereich) ohne Verwaltungs-Seitenleiste
  return route.meta.standalone ? StandaloneLayout : AdminLayout
})
const layoutKey = computed(() => (!authStore.isAuthenticated ? 'auth-layout' : route.meta.standalone ? 'standalone-layout' : 'app-layout'))

/**
 * Initialisierung der Anwendung beim Mounten
 * Lädt gespeicherte Theme-Einstellungen und Authentifizierungsstatus
 */
onMounted(async () => {
  try {
    // Theme aus localStorage laden
    await themeStore.loadThemeFromStorage()

    // Authentifizierungsstatus überprüfen
    if (authStore.token) {
      await authStore.restoreSession()
    }
  } catch (error) {
    console.error('Fehler bei der Anwendungsinitialisierung:', error)
  }
})

/**
 * Theme-Änderungen beobachten und auf das HTML-Element anwenden
 */
watch(
  () => themeStore.currentTheme,
  (newTheme: string) => {
    const htmlElement = document.documentElement

    // Theme-Klassen entfernen und neue hinzufügen
    htmlElement.classList.remove('light', 'dark', 'system')
    htmlElement.classList.add(newTheme)

    // System-Theme Handling
    if (newTheme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      htmlElement.classList.add(systemTheme)
    }
  },
  { immediate: true }
)
</script>

<template>
  <component :is="currentLayout" :key="layoutKey">
    <router-view v-slot="{ Component, route }">
      <transition name="fade" mode="out-in">
        <component :is="Component" :key="route.path" />
      </transition>
    </router-view>
  </component>
</template>

<style scoped>
/**
 * Fade-Transition für Seitennavigation
 * Sorgt für sanfte Übergänge zwischen verschiedenen Views
 */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
